import { Component, OnDestroy, OnInit } from '@angular/core';
import { InspectComponent } from '../../popups/inspect/inspect.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AuthService } from '../../shared/services/auth.service';
import { Subject, catchError, of, takeUntil } from 'rxjs';
import { OrderService } from '../../shared/services/order/order.service';
import { Order } from '../../shared/classes/order';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NgClass, DatePipe } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { NgxPaginationModule } from 'ngx-pagination';

@Component({
    selector: 'app-inspector',
    templateUrl: './inspector.component.html',
    styleUrls: ['./inspector.component.scss'],
    standalone: true,
    imports: [FormsModule, NgClass, MatProgressSpinner, NgxPaginationModule, DatePipe]
})
export class InspectorComponent implements OnInit, OnDestroy {
    public allOrders: Order[];
    public site: string;

    public orderList: Order[];
    public page = 1;
    public isAdmin: boolean;
    public statusFilter = 'All';
    public typeFilter = 'All';
    public smnFilter: string;
    public supplierName: string;
    public idFilter: string;
    public poFilter: string;
    public sortFilter: string;
    public sortAscending = false;
    public listLoaded = false;
    private destroy$ = new Subject<void>();

    constructor(
        private dialog: MatDialog,
        private authService: AuthService,
        private orderService: OrderService,
        private router: Router
    ) {}

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    async ngOnInit() {
        const user = this.authService.user();

        if (!user) {
            void this.router.navigate(['/no-access']);
            return;
        }

        this.isAdmin = user.role === 'Admin';

        if (user.role !== 'Admin' && user.role !== 'Inspector') {
            console.log('User is not an admin. Redirecting to unauthorized page.');
            void this.router.navigate(['/no-access']);
            return;
        }

        this.site = user.site;

        const orders$ = this.orderService.getListLoaded()
            ? this.orderService.allOrdersObserver
            : this.orderService.getSubmittedOrders(user.site === 'Las Angeles' ? 'LasAngeles' : user.site);

        orders$
            .pipe(
                takeUntil(this.destroy$),
                catchError((error) => {
                    console.error('An error occurred:', error);
                    return of([]);
                })
            )
            .subscribe({
                next: (orders) => {
                    this.site = this.site === 'Las Angeles' ? 'Los Angeles' : this.site;
                    this.allOrders = orders;
                    this.filterOrderList(this.sortOrderList);
                    this.listLoaded = true;
                },
                error: (err) => console.error('Error in pipeline:', err),
                complete: () => console.log('Orders Pipeline completed')
            });

        this.orderService.allOrdersObserver.pipe(takeUntil(this.destroy$)).subscribe((orders) => {
            this.orderList = orders;
            this.sortOrderListDesc();
        });
    }

    public selectOrder(orderID: string): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;
        dialogConfig.data = orderID;
        this.dialog.open(InspectComponent, dialogConfig);
    }

    public filterOrderList(filterFn: (key: string) => void, changeOrder = false): void {
        if (!this.allOrders) {
            return;
        }
        this.orderList = this.allOrders.slice().filter((order: Order) => {
            if (order.inspectorStatus !== this.statusFilter && this.statusFilter !== 'All') {
                return false;
            }
            if (
                (order.justDocuments && this.typeFilter === 'Standard') ||
                (!order.justDocuments && this.typeFilter === 'Non-Standard')
            ) {
                return false;
            }
            if (this.smnFilter && !order.SMN.toUpperCase().includes(this.smnFilter.toUpperCase())) {
                return false;
            }
            if (this.idFilter && !order.inspectorID.toUpperCase().includes(this.idFilter.toUpperCase())) {
                return false;
            }
            if (
                this.poFilter &&
                (order.justDocuments || !order.getPO(this.site).toUpperCase().includes(this.poFilter.toUpperCase()))
            ) {
                return false;
            }
            if (this.supplierName && !order.supplierName.toUpperCase().includes(this.supplierName.toUpperCase())) {
                return false;
            }
            return true;
        });

        filterFn.bind(this)(this.sortFilter ?? 'inspectorID', changeOrder);
    }

    public sortOrderList(filter: string, changeOrder = true): void {
        if (changeOrder) {
            this.sortAscending = !this.sortAscending;
        }
        this.sortFilter = filter;
        this.orderList = this.orderList.sort((a: Order, b: Order) => {
            if (a[filter] > b[filter]) {
                return this.sortAscending ? 1 : -1;
            } else {
                return this.sortAscending ? -1 : 1;
            }
        });
    }

    public sortOrderListDesc(): void {
        this.sortAscending = false;
        this.sortFilter = 'submissionDate';
        this.orderList = this.orderList.sort((a: Order, b: Order) => {
            if (a[this.sortFilter] > b[this.sortFilter]) {
                return -1;
            } else {
                return 1;
            }
        });
    }
}
